import { useEffect, useId, useRef, useState } from 'react';
import { formatAddressOneLine, formatSuggestionDisplay } from '../../utils/addressFormat';
import {
  autocompleteUsAddresses,
  getGooglePlacesSetupHint,
  resolvePlaceAddress,
} from '../../utils/googlePlacesClient';
import '../AllCss/AddressAutocompleteInput.css';

/**
 * Single-line US address with Google Places suggestions (via server proxy).
 */
export default function AddressAutocompleteInput({
  id,
  label = 'Address',
  value = {},
  onChange,
  disabled = false,
  placeholder = 'Start typing your address…',
  className = '',
  labelClassName = '',
  inputClassName = '',
  includeCounty = true,
}) {
  const reactId = useId().replace(/:/g, '');
  const inputId = id || `address-autocomplete-${reactId}`;
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const requestRef = useRef(0);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [setupHint, setSetupHint] = useState('');

  useEffect(() => {
    const formatted = formatAddressOneLine(value);
    setQuery(formatted || value.address || '');
  }, [value.address, value.address2, value.city, value.state, value.zipCode]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchSuggestions = async (text) => {
    const q = String(text || '').trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const requestId = ++requestRef.current;
    setLoading(true);
    setSetupHint('');

    try {
      const { suggestions: next } = await autocompleteUsAddresses(q);
      if (requestId !== requestRef.current) return;
      setSuggestions(next);
      setOpen(next.length > 0);
      setActiveIndex(-1);
      if (next.length === 0) {
        setSetupHint('No matches — keep typing or enter your full address manually.');
      }
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setSuggestions([]);
      setOpen(false);
      setSetupHint(getGooglePlacesSetupHint(err));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    setSetupHint('');
    onChange?.({
      address: next,
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      ...(includeCounty ? { countyName: '' } : {}),
    });

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(next);
    }, 280);
  };

  const applySuggestion = async (item) => {
    setLoading(true);
    setSetupHint('');
    try {
      const parsed = await resolvePlaceAddress(item.placeId);
      if (!includeCounty) delete parsed.countyName;
      onChange?.(parsed);
      setQuery(formatAddressOneLine(parsed) || item.description);
      setSuggestions([]);
      setOpen(false);
      setActiveIndex(-1);
    } catch (err) {
      setQuery(item.description);
      onChange?.({ address: item.description });
      setSetupHint(getGooglePlacesSetupHint(err));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      applySuggestion(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className={`address-autocomplete ${className}`.trim()} ref={wrapRef}>
      {label ? (
        <label htmlFor={inputId} className={labelClassName}>
          {label}
        </label>
      ) : null}
      <div className="address-autocomplete-input-wrap">
        <input
          id={inputId}
          type="text"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          className={inputClassName}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${inputId}-listbox`}
          aria-autocomplete="list"
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        {loading ? <span className="address-autocomplete-loading">Searching…</span> : null}
        {open && suggestions.length > 0 ? (
          <ul
            id={`${inputId}-listbox`}
            className="address-autocomplete-suggestions"
            role="listbox"
          >
            {suggestions.map((item, index) => {
              const display = formatSuggestionDisplay(item);
              return (
              <li key={item.placeId || index}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  aria-label={[display.line1, display.line2].filter(Boolean).join(', ')}
                  className={index === activeIndex ? 'is-active' : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applySuggestion(item)}
                >
                  <span className="address-autocomplete-suggestion-primary">{display.line1}</span>
                  {display.line2 ? (
                    <span className="address-autocomplete-suggestion-secondary">{display.line2}</span>
                  ) : null}
                </button>
              </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {setupHint ? <p className="address-autocomplete-hint">{setupHint}</p> : null}
    </div>
  );
}
